import React from "react";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopRingtone } from "../../util/ringtone";
import { appointmentClose } from "../../service/videoCallService";
import { resolveCallExitPath } from "../../util/afterCallNavigate";

/**
 * The call room (Chats.js / TextChat.js) had no error boundary at all, so an
 * unhandled render-time exception — most likely from OpenTok session/publisher
 * refs left in an inconsistent state after a reconnect — unmounted the whole
 * tree with nothing to catch it, producing a completely blank/white screen
 * instead of any redirect or message.
 */
class CallErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this._closed = false;
    this._appointmentId = "";
    this._role = "";
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Call room crashed:", error, info);
    if (this._closed) return;
    this._closed = true;
    try {
      stopRingtone("disconnect");
    } catch (_) {
      /* ignore */
    }
    try {
      stopAllCallMedia();
    } catch (_) {
      /* ignore */
    }
    try {
      stopAllRegisteredMedia();
    } catch (_) {
      /* ignore */
    }
    try {
      const appt = JSON.parse(localStorage.getItem("appointmentData") || "{}");
      const profile = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const appointmentId = appt?.appointmentId || "";
      this._appointmentId = appointmentId;
      this._role = profile?.roleCode || "";
      if (!this._role) {
        const myId = String(profile?._id || "");
        const clientId = String(appt?.clientId || appt?.callerData?.user?.id || "");
        if (myId && clientId && myId === clientId) this._role = "user";
      }
      if (appointmentId) {
        const hadSession = !!(
          appt?.sessionStartTime ||
          appt?.appointmentStartTime ||
          localStorage.getItem("joined") === "true"
        );
        try {
          const liveSocket = window.__prontoLiveSocket;
          liveSocket?.emit("callRejoinFailed", { appointmentId });
          liveSocket?.emit("videoSessionDisconnect", {
            disconnect: true,
            appointmentId,
            reason: "rejoin_failed",
            clientId: appt?.clientId || appt?.callerData?.user?.id,
            consultantId: appt?.consultantId || appt?.callerData?.consultant?.id,
            mediaEstablished: hadSession,
          });
        } catch (_) {
          /* ignore */
        }
        // Billable close so cron does not later label a real session "Missed".
        appointmentClose({
          appointmentId,
          interrupted: false,
          mediaEstablished: hadSession,
          closeReason: hadSession ? "rejoin_failed" : "never_started",
        }).catch((e) => console.error("error-boundary close failed", e));
      }
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
      localStorage.removeItem("callPageLeaveFlag");
    } catch (_) {
      /* ignore */
    }
  }

  handleReturn = () => {
    try {
      window.location.href = resolveCallExitPath({
        appointmentId: this._appointmentId,
        roleCode: this._role,
      });
    } catch (_) {
      window.location.href = "/appointments";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950 p-4 text-white">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-7 text-center shadow-2xl">
            <h2 className="text-xl font-bold">Something went wrong with this call</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/75">
              The call screen ran into a problem. Your camera and microphone have been released.
            </p>
            <button
              type="button"
              onClick={this.handleReturn}
              className="mt-5 w-full rounded-lg bg-prontopsy-blue px-4 py-2 text-sm font-bold text-white">
              Return to my appointments
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CallErrorBoundary;
